// The vitals chart of an inpatient stay (docs/plans/phase2-ward-rounds-nursing.md §4.5).
// Points are the current value of each measurement of each active set: a corrected value
// is drawn, not the one it replaced; anything entered in error is left out. No reference
// ranges are drawn: they are the clinical lead's rules and may not exist.

import { VITAL_SIGNS, isVitalKey, type VitalKey } from './definitions.ts';
import type { ObservationInterpretation } from './rules.ts';
import { parseVitalValue } from './values.ts';

export interface ChartSetInput {
  id: string;
  effectiveAt: string;
  status: 'active' | 'entered_in_error';
  observations: readonly {
    key: string;
    /** As shown, e.g. "37.2". */
    value: string;
    status: 'final' | 'amended' | 'entered_in_error';
    interpretation: ObservationInterpretation;
  }[];
}

export interface ChartPoint {
  setId: string;
  at: string;
  /** The value × 10^decimals of the vital. */
  scaled: number;
  interpretation: ObservationInterpretation;
}

export function vitalChartSeries(sets: readonly ChartSetInput[]): Map<VitalKey, ChartPoint[]> {
  const series = new Map<VitalKey, ChartPoint[]>();
  const ordered = [...sets]
    .filter((s) => s.status === 'active')
    .sort((a, b) => Date.parse(a.effectiveAt) - Date.parse(b.effectiveAt));
  for (const set of ordered) {
    for (const observation of set.observations) {
      if (observation.status !== 'final' || !isVitalKey(observation.key)) continue;
      const parsed = parseVitalValue(observation.key, observation.value);
      if (!parsed.ok) continue;
      const points = series.get(observation.key) ?? [];
      series.set(observation.key, [
        ...points,
        {
          setId: set.id,
          at: set.effectiveAt,
          scaled: parsed.value.scaled,
          interpretation: observation.interpretation,
        },
      ]);
    }
  }
  return series;
}

const PAD_FRACTION_OF_RANGE = 0.1;
const PAD_FRACTION_OF_VALUE = 0.05;

/** The y-axis of one vital's chart, in scaled units, padded so the line isn't on an edge. */
export function chartBounds(
  key: VitalKey,
  values: readonly number[],
): { low: number; high: number } {
  if (values.length === 0) throw new RangeError('A chart needs at least one value');
  const min = Math.min(...values);
  const max = Math.max(...values);
  const oneUnit = 10 ** VITAL_SIGNS[key].decimals;
  const pad = Math.max(
    Math.ceil((max - min) * PAD_FRACTION_OF_RANGE),
    Math.ceil(max * PAD_FRACTION_OF_VALUE),
    oneUnit,
  );
  return { low: Math.max(0, min - pad), high: max + pad };
}
